/**
 * Generic action-approval review card (Automations cross-object actions, design 2026-06-30 §5.3).
 *
 * Renders ONE pending action awaiting human sign-off, for any type the reactive responder
 * proposes — an email reply (`email.send`) or a cross-object create/update/delete on an
 * event / task / note / contact. It is PRESENTATIONAL: the caller passes the flattened
 * `ActionApproval` and an `onDecide` callback (wired to `useDecideActionApproval`), so both the
 * Activity Center (`features/activities`) and the Automations run timeline (`modules/automations`)
 * can reuse it without violating the `features/`-cannot-import-`modules/` layering rule.
 *
 * Security control — CONCRETE-TARGET CONFIRMATION: for an edit/delete the card shows the exact
 * SERVER-RESOLVED target object (`approval.targetObjectId`), so the human approves that specific
 * change, not a description. D7 keeps the target server-owned, so an edit here can never retarget it.
 */
import { useState } from 'react';
import type { ActionApproval, DecideActionApprovalVars } from '@/common/hooks/useActionApprovals';
import './action-approval-card.css';

/** Human-readable novelty flags — the "review this carefully" signals (mirrors the email card). */
const NOVELTY_LABEL: Record<string, string> = {
  new_external_recipient: 'New recipient — not someone you usually email',
  new_recipient: 'New recipient — not someone you usually email',
  large_send: 'Unusually large send',
  untrusted_input: 'Drafted from untrusted content',
};

/** camelCase / snake_case key → readable label, e.g. "startTime" → "Start time". */
function prettyKey(k: string): string {
  const s = k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Render a prepared-action value readably — primitives as-is, objects compactly. */
function prettyVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Payload keys that are plumbing, not user-facing content (hidden from the field list). */
const HIDDEN_PAYLOAD_KEYS = new Set([
  'targetObjectId',
  // targetLabel is surfaced in the dedicated target block, not as a generic field row.
  'targetLabel',
  'threadId',
  'inReplyTo',
  'references',
  'sendFromIntegrationId',
]);

const OP_HINT: Record<ActionApproval['operation'], (t: string) => string> = {
  send: () => 'Drafted reply — review before it sends',
  create: (t) => `New ${t} — review before it's created`,
  update: (t) => `Edit ${t} — review before it changes`,
  delete: (t) => `Delete ${t} — review before it's removed`,
  other: () => 'Proposed action — review before it runs',
};

function objectNoun(a: ActionApproval): string {
  return a.objectType ?? 'item';
}

export function ActionApprovalCard({
  approval,
  onDecide,
  busy = false,
}: {
  approval: ActionApproval;
  /** Mutate-style: the optional `onSuccess` fires only when the decide actually succeeded, so the
   *  edit view can stay open (edit preserved) on a failed decide instead of silently reverting. */
  onDecide: (vars: DecideActionApprovalVars, opts?: { onSuccess?: () => void }) => void;
  busy?: boolean;
}) {
  const isSend = approval.operation === 'send';
  const isTargeted = approval.operation === 'update' || approval.operation === 'delete';
  // FAIL-CLOSED: an edit/delete with no server-resolved target can't be meaningfully confirmed.
  // The happy path never produces this (D6 drops unresolved targets, D7 restores the server-owned
  // one, D5 re-verifies) — but the named confirmation control must refuse a target-less one-tap
  // approve rather than silently omitting the block and leaving the button live.
  const targetMissing = isTargeted && !approval.targetObjectId;
  // FAIL-CLOSED: an unrecognized action type (operation 'other' — e.g. a run-worker snake_case
  // type like delete_object) can't render its concrete consequence (no target confirmation, no
  // delete framing), so a one-tap approve would be uninformed consent. Approve is disabled;
  // reject stays available to clear the queue.
  const unrecognized = approval.operation === 'other';
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(approval.email?.body ?? '');
  const bodyId = `action-approval-body-${approval.id}`;
  const noun = objectNoun(approval);

  const fields = Object.entries(approval.preparedPayload).filter(
    ([k, v]) => !HIDDEN_PAYLOAD_KEYS.has(k) && v !== undefined && v !== null && v !== '',
  );

  return (
    <div className="aac-card" role="group" aria-label={OP_HINT[approval.operation](noun)}>
      <div className="aac-card__hint">{OP_HINT[approval.operation](noun)}</div>

      {approval.noveltyFlags.length > 0 && (
        <div className="aac-card__warn" role="note">
          {approval.noveltyFlags.map((f) => (
            <span key={f} className="aac-card__warn-line">
              ⚠ {NOVELTY_LABEL[f] ?? f}
            </span>
          ))}
        </div>
      )}

      {approval.incomingSummary && (
        <div className="aac-card__summary" role="note">
          <span className="aac-card__summary-label">In response to</span>
          <p className="aac-card__summary-text">{approval.incomingSummary}</p>
        </div>
      )}

      {/* CONCRETE-TARGET CONFIRMATION — the exact server-resolved object an edit/delete will change.
          Shows the human-readable label (WHAT is changing) as the primary line, with the objectId
          kept as secondary text for traceability; falls back to the id alone when no label resolved. */}
      {isTargeted && approval.targetObjectId && (
        <div className="aac-card__target" role="note">
          <span className="aac-card__target-label">
            {approval.operation === 'delete' ? 'Deletes this' : 'Edits this'} {noun}
          </span>
          {approval.targetLabel ? (
            <>
              <span className="aac-card__target-name" data-testid="aac-target-name">
                {approval.targetLabel}
              </span>
              <span
                className="aac-card__target-id aac-card__target-id--secondary"
                data-testid="aac-target-id"
              >
                {approval.targetObjectId}
              </span>
            </>
          ) : (
            <span className="aac-card__target-id" data-testid="aac-target-id">
              {approval.targetObjectId}
            </span>
          )}
        </div>
      )}

      {/* FAIL-CLOSED — an edit/delete whose target didn't resolve can't be confirmed; warn and the
          primary approve button below is disabled (reject/dismiss stays available). */}
      {targetMissing && (
        <div className="aac-card__warn" role="alert" data-testid="aac-target-unresolved">
          <span className="aac-card__warn-line">
            ⚠ Unresolved target — this {noun} can’t be confirmed and won’t run.
          </span>
        </div>
      )}

      {/* FAIL-CLOSED — an unrecognized action type can't show what approving would do. */}
      {unrecognized && (
        <div className="aac-card__warn" role="alert" data-testid="aac-unrecognized">
          <span className="aac-card__warn-line">
            ⚠ Unrecognized action type ({approval.actionType}) — it can’t be reviewed here and
            won’t run. You can reject it to clear the queue.
          </span>
        </div>
      )}

      {isSend ? (
        <>
          <dl className="aac-card__meta">
            <div className="aac-card__row">
              <dt className="aac-card__k">To</dt>
              <dd className="aac-card__v">{approval.email?.to || '—'}</dd>
            </div>
            <div className="aac-card__row">
              <dt className="aac-card__k">Subject</dt>
              <dd className="aac-card__v">{approval.email?.subject || '—'}</dd>
            </div>
          </dl>
          {editing ? (
            <label className="aac-card__body-label" htmlFor={bodyId}>
              <span className="aac-card__k">Body</span>
              <textarea
                id={bodyId}
                className="aac-card__body-edit"
                value={body}
                rows={6}
                disabled={busy}
                onChange={(e) => setBody(e.target.value)}
              />
            </label>
          ) : (
            <div className="aac-card__body" aria-label="Email body">
              {approval.email?.body || '—'}
            </div>
          )}
        </>
      ) : fields.length > 0 ? (
        <dl className="aac-card__meta">
          {fields.map(([k, v]) => (
            <div key={k} className="aac-card__row">
              <dt className="aac-card__k">{prettyKey(k)}</dt>
              <dd className="aac-card__v">{prettyVal(v)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        approval.operation !== 'delete' && (
          <div className="aac-card__body">No further details.</div>
        )
      )}

      {approval.notes && (
        <div className="aac-card__notes" role="note">
          <span className="aac-card__notes-label">
            Note from Xavior — only you see this, it isn’t part of the action
          </span>
          <p className="aac-card__notes-text">{approval.notes}</p>
        </div>
      )}

      <div className="aac-card__actions">
        {isSend && editing ? (
          <>
            <button
              type="button"
              className="aac-btn aac-btn--primary"
              disabled={busy}
              onClick={() =>
                onDecide(
                  {
                    id: approval.id,
                    decision: 'edited',
                    preparedPayload: { ...approval.preparedPayload, body },
                  },
                  // Close the editor only when the decide SUCCEEDS — a failed decide keeps the
                  // edit on screen instead of silently reverting to the unedited draft (which
                  // invites approving the very text the user just corrected).
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              Save &amp; send
            </button>
            <button
              type="button"
              className="aac-btn"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setBody(approval.email?.body ?? '');
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="aac-btn aac-btn--primary"
              disabled={busy || targetMissing || unrecognized}
              onClick={() => onDecide({ id: approval.id, decision: 'approved' })}
            >
              {approval.operation === 'delete' ? 'Delete' : isSend ? 'Send' : 'Approve'}
            </button>
            {isSend && (
              <button
                type="button"
                className="aac-btn"
                disabled={busy}
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            )}
          </>
        )}
        <button
          type="button"
          className="aac-btn aac-btn--danger"
          disabled={busy}
          onClick={() => onDecide({ id: approval.id, decision: 'rejected' })}
        >
          {isSend ? 'Discard' : 'Reject'}
        </button>
      </div>
    </div>
  );
}
